/**
 * THE HOUSEHOLD AT A GLANCE (ops #84, `docs/plan-client-workplace.md` section 4): the band of facts at
 * the top of the client's page. On a phone it is one card above the people; on a desk a band across
 * the top. It replaces no card the page has: the Paying card and the Asks card stay where they are, and
 * the asks line links to the Asks card.
 *
 * FACTS IN FIXED SENTENCES, NEVER A SUMMARY. The server answers facts (`GET /api/clients/:id/glance`):
 * a day, a window, a sum, a count, a state, a reason's word. Each line below is a sentence written here
 * once, with those facts filled in. Nothing composes text about the household: a summary a person did
 * not write is a summary nobody can be asked about, and CLAUDE.md refuses any AI writing text at all.
 *
 * THE MONEY ALONE WAITS FOR A FRESH SIGN-IN. The glance is read as the page opens, and the server never
 * refuses it for a stale sign-in: it holds back what is owed and hands the way to sign in again, which
 * this draws as a link in the line's place. Every other line is drawn either way.
 *
 * A WORKER OLDER THAN THE GLANCE answers 404, and the band is then not drawn at all: the page is the
 * page it was. Any other failure says so in one line, with a press to read it again.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AGREEMENT_STATE_LABELS,
  CADENCE_LABELS,
  DO_NOT_CONTACT_REASON_LABELS,
  PRICE_SOURCE_LABELS,
  SERVICE_LINE_LABELS,
  type DoNotContactReason,
} from '@fk/shared';
import { ApiError, getClientGlance, type ClientGlance } from '../api.ts';
import { formatInstant, formatWindow, mediumDay, money, shortDay } from '../format.ts';
import { Card } from './AppShell.tsx';
import { Button } from './Field.tsx';

/** "Jo", "Jo and Maria", "Ana, Jo and Maria". */
function listed(names: readonly string[]): string {
  return names.length < 2
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** A reason's word as it reads inside a sentence: "they asked us". */
const reasonWords = (reason: DoNotContactReason): string => {
  const words = DO_NOT_CONTACT_REASON_LABELS[reason];
  return words.charAt(0).toLowerCase() + words.slice(1);
};

const lowerFirst = (words: string): string => words.charAt(0).toLowerCase() + words.slice(1);

export function AtAGlance({ clientId }: { clientId: string }) {
  const place = useLocation();
  const returnPath = `${place.pathname}${place.search}`;
  const [glance, setGlance] = useState<ClientGlance | null>(null);
  const [failed, setFailed] = useState<'none' | 'absent' | 'failed'>('none');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setFailed('none');
    getClientGlance(clientId, returnPath)
      .then((answer) => {
        if (live) setGlance(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setFailed(thrown instanceof ApiError && thrown.status === 404 ? 'absent' : 'failed');
      });
    return () => {
      live = false;
    };
    // The way back is read when the glance is asked for; it is asked again for a household, not a filter.
  }, [clientId, attempt]);

  if (failed === 'absent') return null;
  if (failed === 'failed') {
    return (
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p className="text-sm text-navy/70">The summary of this household did not load.</p>
        <Button tone="quiet" onClick={() => setAttempt((n) => n + 1)}>
          Read it again
        </Button>
      </div>
    );
  }
  if (glance === null) return null;

  return (
    <Card label="At a glance" className="mb-4">
      <dl className="@container grid gap-x-6 gap-y-3 md:grid-cols-2 xl:grid-cols-3">
        <Line caption="Next visit">
          <NextVisit visit={glance.nextVisit} />
        </Line>
        <Line caption="Owed">
          <Owed owed={glance.owed} />
        </Line>
        <Line caption="Asks from home">
          {glance.asks.waiting === 0 ? (
            'Nothing waits on the office.'
          ) : (
            <>
              {glance.asks.waiting} waiting on the office, the oldest since{' '}
              {formatInstant(glance.asks.oldestAt)}.{' '}
              <a href="#asks-from-home" className="font-medium underline">
                See them
              </a>
            </>
          )}
        </Line>
        <Line caption="Last contact">
          {glance.lastContact === null ? (
            'Nothing in the last thirteen months.'
          ) : (
            <>
              <span className="block">{glance.lastContact.title}</span>
              <span className="block text-xs text-navy/65">
                {glance.lastContact.on
                  ? mediumDay(glance.lastContact.on)
                  : formatInstant(glance.lastContact.at)}
                {glance.lastContact.who ? `, ${glance.lastContact.who.name}` : ''}
              </span>
            </>
          )}
        </Line>
        <Line caption="Can be reached">
          {glance.reach.length === 0 ? (
            'Nobody is on file.'
          ) : (
            <ul className="flex flex-col gap-1">
              {glance.reach.map((person) => (
                <li key={person.contactId}>{reachWords(person)}</li>
              ))}
            </ul>
          )}
        </Line>
        <Line caption="Agreements">
          {glance.agreements.length === 0 ? (
            'No agreement stands.'
          ) : (
            <ul className="flex flex-col gap-1">
              {glance.agreements.map((agreement) => (
                <li key={agreement.id}>
                  <Link to={`/agreements/${agreement.id}`} className="underline">
                    {agreement.homeLabel ? `${agreement.homeLabel}: ` : ''}
                    {agreementWords(agreement, glance.today)}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Line>
        <Line caption="On home">
          {glance.onHome.length === 0
            ? 'Nobody yet.'
            : `${glance.onHome
                .map((door) => `${door.name} ${door.state === 'granted' ? 'signed in' : 'invited'}`)
                .join('; ')}.`}
        </Line>
      </dl>
    </Card>
  );
}

function Line({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs tracking-wide text-navy/55 uppercase">{caption}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function NextVisit({ visit }: { visit: ClientGlance['nextVisit'] }) {
  if (visit === null) return <>Nothing booked ahead.</>;
  const words = [
    shortDay(visit.scheduledDate),
    formatWindow(visit.windowStart, visit.windowEnd) ?? 'no window set',
    visit.homeLabel,
    visit.cleaners.length > 0 ? `with ${listed(visit.cleaners)}` : 'nobody on it yet',
  ]
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join(', ');
  return (
    <Link to={`/visits/${visit.id}`} className="underline">
      {visit.status === 'in_progress' ? `Under way now: ${words}` : words}
    </Link>
  );
}

function Owed({ owed }: { owed: ClientGlance['owed'] }) {
  if (!owed.shown) {
    return (
      <a href={owed.signInPath} className="font-medium underline">
        Sign in again to see what is owed
      </a>
    );
  }
  if (owed.cents <= 0) return <>Nothing owed.</>;
  return (
    <>
      {money(owed.cents)} on {owed.invoices} {owed.invoices === 1 ? 'invoice' : 'invoices'}
      {owed.oldestDueOn ? `, the oldest due ${mediumDay(owed.oldestDueOn)}` : ''}.
      {owed.overdue ? <span className="text-coral"> Some of it is past due.</span> : null}
    </>
  );
}

/** One person's line: texts, email and calls, each with the mark that holds it and why, and a hold. */
function reachWords(person: ClientGlance['reach'][number]): string {
  const texts = person.texts
    ? 'texts yes, on the box they ticked on the website'
    : person.marks.sms
      ? `texts no, marked do not contact, ${reasonWords(person.marks.sms)}`
      : 'texts no';
  const email = person.email
    ? 'email yes'
    : person.marks.email
      ? `email no, marked do not contact, ${reasonWords(person.marks.email)}`
      : 'email no';
  const calls =
    person.calls === 'no_number'
      ? 'no number to call'
      : person.calls === 'marked'
        ? `calls marked do not call${person.marks.call ? `, ${reasonWords(person.marks.call)}` : ''}`
        : 'calls open';
  const parts = [texts, email, calls];
  if (person.held) parts.push('a stop in their own words waits on the office');
  return `${person.name}: ${parts.join('; ')}.`;
}

/**
 * A PAUSE IN WORDS, only where one stands on `today` or is still to come (plan section 4): "Paused from
 * Sep 1, 2026 until Sep 30, 2026." or "Pauses from Oct 1, 2026, no return date yet." A pause already
 * over says nothing, since resuming a series keeps the day it began; the server leaves one out too,
 * and this holds for an answer from a Worker older than that.
 */
export function pauseWords(
  from: string | null,
  until: string | null,
  today: string,
): string | null {
  if (from === null || (until !== null && until < today)) return null;
  const verb = from > today ? 'Pauses' : 'Paused';
  return until === null
    ? `${verb} from ${mediumDay(from)}, no return date yet.`
    : `${verb} from ${mediumDay(from)} until ${mediumDay(until)}.`;
}

/** "House cleaning, every 2 weeks, $212.00, published card, revision 1. Active since Sep 1, 2026." */
function agreementWords(agreement: ClientGlance['agreements'][number], today: string): string {
  const price = `${money(agreement.priceCents)}, ${lowerFirst(PRICE_SOURCE_LABELS[agreement.priceSource])}${
    agreement.cardRevision === null ? '' : `, revision ${agreement.cardRevision}`
  }`;
  const pause = pauseWords(agreement.pausedFrom, agreement.pausedUntil, today);
  return `${SERVICE_LINE_LABELS[agreement.serviceLine]}, ${lowerFirst(
    CADENCE_LABELS[agreement.cadence],
  )}, ${price}. ${AGREEMENT_STATE_LABELS[agreement.state]} since ${mediumDay(agreement.startedOn)}.${
    pause === null ? '' : ` ${pause}`
  }`;
}
